<script setup lang="ts">
import { computed, inject } from 'vue';

import { configuratorStateKey } from '../ProductConfiguratorKeys';
import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import ConfiguratorSummaryRow from './ConfiguratorSummaryRow.vue';
import { getAddonTitle } from '@/helpers/printess';

const { formatPrice, cart, isAuthorized, channel } = useContext();
const { websiteText } = useWebsiteTexts();
const { configuratorState } = inject(configuratorStateKey);

const props = defineProps({
  variants: {
    type: Array,
    required: true
  },
  decorations: {
    type: Array,
    required: true
  },
  canAddToCart: {
    type: Boolean,
    required: true
  },
  hasDecoration: {
    type: Boolean,
    required: true
  },
  hasAddons: {
    type: Boolean,
    required: true
  },
  isGiftCard: {
    type: Boolean,
    default: false
  },
  giftCardFreightPrice: {
    type: Number,
    default: 0
  }
});

const emptySummary = computed(
  () => !props.canAddToCart && !props.hasDecoration && !props.hasAddons
);
const displayAddProductText = computed(
  () => !props.canAddToCart && (props.hasDecoration || props.hasAddons)
);
const displayAdditionalFeeText = computed(
  () => selectedDecorations.value?.[0]?.addons?.length
);

const selectedVariants = computed(() => {
  if (!props.canAddToCart) return [];
  const selected = props.variants.filter((v) => {
    return configuratorState.value.quantities[v.systemId];
  });

  let variants = selected.map(s => {
    const isFlexible = s.fields.IsFlexibleType == true;
    const quantity = configuratorState.value.quantities[s.systemId];
    const price = (isFlexible ? configuratorState.value.customPrice : configuratorState.value.activePriceBracket[s.systemId]?.price) ?? 0;
    const originalPrice = getOriginalPrice(s);
    const currentPrice = getCurrentPrice(s);
    const campaignPercentage = getCampaignPercentage(
      currentPrice,
      originalPrice
    );
    const articleNumberForDisplay = s.fields.NAVArticleNumber || s.articleNumber;
    const country =
      configuratorState.value.recipientCountry.name ||
      cart?.value.countries[0].text;
    const giftcardType = configuratorState.value.giftcardType;
    return {
      name:
        s.fields?.Color?.[0]?.name ||
        s.fields?.Color?.[0]?.value ||
        s.fields?.GiftcardType + ' - ' + country + ' - ' + giftcardType,
      id: s.articleNumber,
      quantity,
      price,
      originalPrice,
      currentPrice,
      campaignPercentage,
      articleNumberForDisplay: articleNumberForDisplay
    };
  });
  return variants;
});

const selectedDecorations = computed(() => {
  if (!props.hasDecoration) return [];

  const placement = props.decorations.filter((v) => {
    return v.systemId === configuratorState.value.selections.selectedPlacement;
  });

  return placement.map((p) => {
    const technique = p.techniques.find(
      (t) => t.systemId === configuratorState.value.selections.selectedTechnique
    );
    const techniqueVariant = technique.variants.find(
      (v) =>
        v.systemId ===
        configuratorState.value.selections.selectedTechniqueVariant
    );
    const techniqueCode = technique.code;
    const variantType = techniqueVariant.nrOfColors;
    const name = websiteText(
      `decoration__technique_${techniqueCode.toLowerCase()}`
    ).value;
    let variantName = techniqueVariant.name || '';
    if (variantType && !techniqueVariant.name) {
      variantName =
        variantType === 1
          ? websiteText('decoration__variant_color_single', {
              amount: variantType
            }).value
          : websiteText('decoration__variant_color_multiple', {
              amount: variantType
            }).value;
    }

    const priceBracket =
      configuratorState.value.selections?.selectedTechniqueVariantPrices?.reduce(
        (curr, b) => {
          return b.bracket > curr.bracket &&
            b.bracket <= Math.max(configuratorState.value.sumQuantity, 1)
            ? b
            : curr;
        },
        { bracket: 0, price: 0 }
      );

    const displayName = variantName || name;
    const quantity = configuratorState.value.sumQuantity || 1;
    const price = priceBracket?.price || 0;
    const addons =
      configuratorState.value.selections.selectedTechniqueVariantAddons;

    return {
      name: displayName,
      id: p.systemId,
      quantity,
      price,
      addons
    };
  });
});

const selectedAddons = computed(() => {
  if (!props.hasAddons) return [];

  return configuratorState.value.selections.selectedAddons.map((a) => {
    return {
      name: getAddonTitle(a.variant),
      id: a.systemId,
      quantity: a.variant.ignoreGiftCardQuantity
        ? 1
        : configuratorState.value.sumQuantity,
      price: a.variant.prices[0]?.price || 0
    };
  });
});

const giftcardShipping = computed(() => {
  if (!props.isGiftCard) return [];

  return [
    {
      name: websiteText('giftcard__shipping_name').value,
      id: 'PKFRAKT',
      quantity: configuratorState.value.sumQuantity,
      price: props.giftCardFreightPrice
    }
  ];
});

function getCurrentPrice(row) {
  return row.campaignPrice ?? row.price;
}

function getOriginalPrice(row) {
  return row.originalPrice >= row.price ? row.originalPrice : row.price;
}

function getCampaignPercentage(current, original) {
  if (current < original) {
    return formatPrice(
      Math.abs(((original - current) / original) * -100),
      undefined,
      '',
      '',
      'decimal'
    );
  }

  return 0;
}

const summaryProducts = computed(() => {
  return selectedVariants.value.reduce((acc, curr) => {
    var price = curr.price ?? curr.currentPrice ?? curr.originalPrice;
    if (
      channel.value.combineGiftCardPriceWithShipping &&
      props.isGiftCard &&
      !isAuthorized()
    ) {
      price += props.giftCardFreightPrice;
    }
    return acc + price * curr.quantity;
  }, 0);
});

const summaryDecorations = computed(() => {
  return selectedDecorations.value.reduce((acc, curr) => {
    return acc + curr.price * curr.quantity + summaryFees.value;
  }, 0);
});

const summaryAddons = computed(() => {
  return selectedAddons.value.reduce((acc, curr) => {
    return acc + curr.price * curr.quantity + summaryFees.value;
  }, 0);
});

const summaryShipping = computed(() => {
  if (
    !props.isGiftCard ||
    (channel.value.combineGiftCardPriceWithShipping &&
      props.isGiftCard &&
      !isAuthorized())
  )
    return 0;
  return configuratorState.value.sumQuantity * props.giftCardFreightPrice;
});

const summaryFees = computed(() => {
  return selectedDecorations.value.reduce((acc, curr) => {
    return (
      acc + (curr.addons || 0) &&
      curr.addons.reduce((addonAcc, addon) => addonAcc + addon.price, 0)
    );
  }, 0);
});

const summaryTotal = computed(() => {
  return (
    summaryProducts.value +
    summaryDecorations.value +
    summaryAddons.value +
    summaryShipping.value
  );
});
</script>

<template>
  <div class="config-summary">
    <h5 class="config-summary__title">
      {{ websiteText('checkout__summary').value }}
    </h5>
    <div class="config-summary__content" v-if="!emptySummary">
      <div class="config-summary__products">
        <div
          :class="[
            'config-summary__products-summary',
            { 'config-summary__products-summary--faded': displayAddProductText }
          ]"
        >
          <div class="config-summary__products-title">
            {{ websiteText('checkout__summary_products').value }}
          </div>
          <div class="config-summary__products-total">
            {{ formatPrice(summaryProducts) }}
          </div>
        </div>
        <div class="config-summary__product" v-if="selectedVariants.length">
          <ConfiguratorSummaryRow
            v-for="variant in selectedVariants"
            :key="variant.id"
            :is-decoration="false"
            :item="variant"
            :gift-card-freight-price="$props.giftCardFreightPrice"
            :is-gift-card="props.isGiftCard"
          >
          </ConfiguratorSummaryRow>
        </div>
        <div
          class="config-summary__add-product-info"
          v-if="displayAddProductText"
        >
          {{ websiteText('decoration__add_product_info').value }}
        </div>
      </div>
      <div
        v-if="selectedDecorations.length"
        class="config-summary__decorations"
      >
        <div class="config-summary__decorations-summary">
          <div class="config-summary__decorations-title">
            {{ websiteText('decoration__decorations').value }}
          </div>
          <div class="config-summary__decorations-total">
            {{ formatPrice(summaryDecorations) }}
          </div>
        </div>
        <div class="config-summary__decoration">
          <ConfiguratorSummaryRow
            v-for="variant in selectedDecorations"
            :key="variant.id"
            :is-decoration="true"
            :item="variant"
          >
          </ConfiguratorSummaryRow>
        </div>
      </div>
      <div v-if="selectedAddons.length" class="config-summary__addons">
        <div class="config-summary__addons-summary">
          <div class="config-summary__addons-title">
            {{ websiteText('giftcard__addons').value }}
          </div>
          <div class="config-summary__addons-total">
            {{ formatPrice(summaryAddons) }}
          </div>
        </div>
        <div class="config-summary__addons">
          <ConfiguratorSummaryRow
            v-for="variant in selectedAddons"
            :key="variant.id"
            :is-decoration="true"
            :item="variant"
          >
          </ConfiguratorSummaryRow>
        </div>
      </div>
      <div
        v-if="isGiftCard && selectedVariants.length && summaryShipping > 0"
        class="config-summary__shipping"
      >
        <div class="config-summary__shipping-summary">
          <div class="config-summary__shipping-title">
            {{ websiteText('giftcard__shipping').value }}
          </div>
          <div class="config-summary__shipping-total">
            {{ formatPrice(summaryShipping) }}
          </div>
        </div>
        <div class="config-summary__addons">
          <ConfiguratorSummaryRow
            v-for="variant in giftcardShipping"
            :key="variant.id"
            :is-decoration="true"
            :item="variant"
          >
          </ConfiguratorSummaryRow>
        </div>
      </div>
      <div class="config-summary__total">
        <div class="config-summary__total-title">
          {{ websiteText('cart__total').value }}
        </div>
        <div class="config-summary__total-price">
          {{ formatPrice(summaryTotal) }}
        </div>
      </div>
    </div>
    <p
      class="config-summary__fee-info p-short"
      v-if="displayAdditionalFeeText && canAddToCart"
    >
      {{
        websiteText('decoration__additional_fee', {
          amount: formatPrice(summaryFees)
        }).value
      }}
    </p>
    <p class="config-summary__empty p-short" v-if="emptySummary">
      {{ websiteText('decoration__empty_summary').value }}
    </p>
  </div>
</template>

<style>
.config-summary {
  padding: 16px 20px 16px;
  border: 1px solid var(--color-neutrals-50);
  border-radius: 3px;
}

.config-summary__title {
  margin: 0 0 20px;
  color: var(--color-text-darker);
}

.config-summary__fee-info,
.config-summary__empty {
  color: var(--color-neutrals-80);
  font-style: italic;
  letter-spacing: 0.015em;
  margin: 0;
  padding-bottom: 9px;
}

.config-summary__fee-info {
  padding: 20px 10px 10px;
}

.config-summary__products-summary,
.config-summary__decorations-summary,
.config-summary__addons-summary,
.config-summary__shipping-summary,
.config-summary__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  line-height: 19px;
  color: var(--color-text-darker);
  font-weight: 700;
}

.config-summary__products-summary {
  margin-bottom: 4px;
}

.config-summary__decorations-summary,
.config-summary__addons-summary,
.config-summary__shipping-summary {
  margin-bottom: 8px;
  margin-top: 20px;
}

.config-summary__total {
  font-size: 20px;
  line-height: 32px;
  margin-top: 20px;
}

.config-summary__products-summary--faded,
.config-summary__add-product-info {
  color: var(--color-text-light);
}

.config-summary__add-product-info {
  font-style: italic;
  font-size: 12px;
  line-height: 16px;
  padding: 0 10px;
}
</style>
